import {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  PropsWithChildren,
} from 'react';

export interface LocalStorageContextProps {
  setToStorage: (key: string, value: any) => void;
  getFromStorage: <T>(key: string) => T | null;
  removeFromStorage: (key: string) => void;
}

const context = createContext<LocalStorageContextProps>({
  setToStorage: () => null,
  getFromStorage: () => null,
  removeFromStorage: () => null,
});

const { Provider } = context;
const { localStorage } = window;

const LocalStorageProvider = ({ children }: PropsWithChildren) => {
  const [store, setStore] = useState<Record<string, any>>({});

  const setToStorage = useCallback((key: string, value: any) => {
    setStore((prev) => ({ ...prev, [key]: value }));
    localStorage.setItem(key, JSON.stringify(value));
  }, []);

  const getFromStorage = useCallback(<T,>(key: string) => {
    const item = localStorage.getItem(key);
    return item === null ? null : (JSON.parse(item) as T);
  }, []);

  const removeFromStorage = useCallback(
    (key: string) => {
      const storeCopy = Object.entries(store)
        .filter(([itemKey]) => itemKey !== key)
        .reduce((acc, [itemKey, value]) => ({ ...acc, [itemKey]: value }), {});

      setStore(storeCopy);
      localStorage.removeItem(key);
    },
    [store]
  );

  const memoisedContext = useMemo(
    () => ({
      setToStorage,
      getFromStorage,
      removeFromStorage,
    }),
    [setToStorage, getFromStorage, removeFromStorage]
  );

  return <Provider value={memoisedContext}>{children}</Provider>;
};

const useLocalStorage = () => useContext(context);

export { LocalStorageProvider, useLocalStorage };
